import { useEffect, useState } from "react";
import { Download, Trash2, Smartphone, Camera, ShieldBan } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, BACKEND, getToken } from "@/lib/api";
import { PageHeader, StatusChip, fmtDate } from "@/components/common";
import { getQueue, discardItem, syncQueue } from "@/lib/offline";

const NAMES = { auth: "Authentication", database: "Database", storage: "Private file storage", ai: "AI vision / OCR & email drafts", qr: "QR decoding", email: "Email sending", transcription: "Voice transcription", mailbox: "Mailbox (reply detection)", google_mail: "Google sign-in for Gmail", microsoft: "Microsoft 365 sign-in" };

export default function Settings() {
  const [i, setI] = useState(null);
  const [supp, setSupp] = useState([]);
  const [email, setEmail] = useState("");
  const [queue, setQueue] = useState([]);
  const load = () => {
    api.get("/integrations").then((r) => setI(r.data));
    api.get("/suppressions").then((r) => setSupp(r.data));
    getQueue().then(setQueue);
  };
  useEffect(() => { load(); }, []);
  const add = async () => { try { await api.post("/suppressions", { email }); setEmail(""); load(); } catch (e) { toast.error(errMsg(e)); } };
  const del = async (s) => { await api.delete(`/suppressions/${s.id}`); load(); };

  return (
    <div className="space-y-6">
      <PageHeader title="Settings & integrations" />
      <section className="cd-card" data-testid="integrations-panel">
        <h2 className="mb-3 text-lg font-bold">Integrations</h2>
        {i && Object.entries(NAMES).map(([k, n]) => (
          <div key={k} className="flex flex-col gap-1 border-b border-slate-100 py-3 last:border-0 sm:flex-row sm:items-center sm:justify-between" data-testid={`integration-${k}`}>
            <div><p className="font-semibold">{n}</p><p className="text-xs text-slate-500">{i[k].detail}</p></div>
            <StatusChip status={i[k].status} />
          </div>
        ))}
      </section>
      <section className="cd-card" data-testid="usage-panel">
        <h2 className="mb-2 text-lg font-bold">AI usage & limits</h2>
        {i && <>
          {Object.keys(i.ai_usage).length === 0 && <p className="text-sm text-slate-500">No AI calls yet.</p>}
          {Object.entries(i.ai_usage).map(([k, u]) => <p key={k} className="text-sm">{k.replace(/_/g, " ")}: <b>{u.calls}</b> calls · {(u.input_chars / 1000).toFixed(1)}k chars in / {(u.output_chars / 1000).toFixed(1)}k out</p>)}
          <p className="mt-2 text-xs text-slate-500">Cost estimates are not shown because pricing is not configured. Usage is billed to the AI credit balance of the hosting account.</p>
          <p className="mt-3 text-sm">Batch limit: {i.limits.max_batch_files} images · {i.limits.max_file_mb} MB per file · Send rate: {i.limits.send_rate_per_min}/min · Daily limit: {i.limits.daily_send_limit} (sent in last 24h: {i.limits.sent_last_24h})</p>
        </>}
      </section>
      <section className="cd-card" data-testid="offline-panel">
        <h2 className="mb-2 flex items-center gap-2 text-lg font-bold"><Smartphone className="h-5 w-5" />This device</h2>
        <p className="text-sm text-slate-600">Captures made while offline are kept in this browser and are <b>not backed up until synced</b>. They upload automatically when you're online with the app open — browsers don't allow reliable background upload while the app is closed.</p>
        {queue.length > 0 ? (
          <div className="mt-3 space-y-2">
            {queue.map((q) => (
              <div key={q.client_id} className="flex items-center justify-between rounded-xl bg-orange-50 p-2 text-sm">
                <span>{q.kind} · {fmtDate(q.created_at)} {q.error && <span className="text-rose-600">· {String(q.error)}</span>}</span>
                <button onClick={async () => { if (window.confirm("Discard this local capture? It has not been uploaded.")) { await discardItem(q.client_id); load(); } }} className="text-rose-600"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
            <button data-testid="sync-now-btn" onClick={async () => { await syncQueue(); load(); }} className="cd-btn-secondary">Sync now</button>
          </div>
        ) : <p className="mt-2 text-sm font-semibold text-emerald-700" data-testid="device-synced">Everything on this device is synced.</p>}
        <p className="mt-4 flex gap-2 text-sm text-slate-600"><Camera className="mt-0.5 h-4 w-4 shrink-0" />Camera: card photos use your phone's camera app. QR scanning asks for camera permission in the browser; if denied, you can upload a QR image instead. Install RingUs via your browser's “Add to Home screen” / “Install app”.</p>
      </section>
      <section className="cd-card" data-testid="suppression-panel">
        <h2 className="mb-2 flex items-center gap-2 text-lg font-bold"><ShieldBan className="h-5 w-5" />Suppression list</h2>
        <p className="mb-3 text-sm text-slate-600">Unsubscribed and do-not-contact addresses are excluded from all future emails. Unsubscribe links in emails add addresses here automatically.</p>
        <div className="flex gap-2">
          <input data-testid="suppression-email-input" className="cd-input" placeholder="email@company.com" value={email} onChange={(e) => setEmail(e.target.value)} />
          <button data-testid="suppression-add-btn" onClick={add} className="cd-btn-primary">Add</button>
        </div>
        <div className="mt-3 space-y-1">{supp.map((s) => (
          <div key={s.id} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-sm" data-testid={`suppression-${s.email}`}><span>{s.email} <span className="text-xs text-slate-500">· {s.reason}</span></span><button onClick={() => del(s)} className="text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button></div>
        ))}</div>
      </section>
      <section className="cd-card">
        <h2 className="mb-2 text-lg font-bold">Data export</h2>
        <button data-testid="settings-export-csv-btn" onClick={() => { window.location.href = `${BACKEND}/api/contacts/export.csv?auth=${getToken()}`; }} className="cd-btn-secondary"><Download className="h-4 w-4" />Export contacts (CSV)</button>
        <p className="mt-2 text-xs text-slate-500">Includes tags, event, notes and follow-up status. Original images can be downloaded from each contact.</p>
      </section>
    </div>
  );
}
